import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { filesAPI, ticketsAPI, dynoAPI, adminAPI } from '../../services/api';
import FileMatchingPanel from '../../components/FileMatchingPanel';
import { 
  ArrowLeft, Download, Clock, CheckCircle, AlertCircle, 
  RefreshCw, Send, MessageSquare, FileText, User, Calendar,
  LifeBuoy, Upload, Image, Loader2, Binary, StickyNote,
  ChevronLeft, ChevronRight, Trash2, Tag, GitBranch, 
  AlertTriangle, Plus, Paperclip, BarChart3, Activity,
  ChevronDown, ChevronUp, Gauge, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

// ======================== HEX VIEWER COMPONENT ========================
const HexViewer = ({ fileId }) => {
  const [hexData, setHexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const PAGE_SIZE = 512;

  const fetchHex = useCallback(async (newOffset) => {
    setLoading(true);
    try {
      const res = await filesAPI.getHexPreview(fileId, newOffset, PAGE_SIZE);
      setHexData(res.data);
      setOffset(newOffset);
    } catch (err) {
      console.error('Failed to load hex preview:', err);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => { if (expanded) fetchHex(0); }, [expanded, fetchHex]);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="glass-card p-4 w-full text-left hover:border-[#EC2229]/30 transition-all group"
        data-testid="hex-viewer-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="bg-[#EC2229]/10 p-2 rounded-lg">
            <Binary className="w-5 h-5 text-[#EC2229]" />
          </div>
          <div>
            <p className="text-sm font-medium text-white group-hover:text-[#EC2229] transition-colors">HEX Vorschau</p>
            <p className="text-xs text-slate-500">Datei-Inhalt anzeigen</p>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="glass-card p-5 overflow-hidden" data-testid="hex-viewer">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Binary className="w-4 h-4 text-[#EC2229]" />
          HEX Vorschau
        </h3>
        <button onClick={() => setExpanded(false)} className="text-xs text-slate-500 hover:text-white transition-colors">
          Schließen
        </button>
      </div>

      {hexData && (
        <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
          <span>{hexData.filename} ({(hexData.file_size / 1024).toFixed(1)} KB)</span>
          <span>Offset: 0x{offset.toString(16).toUpperCase().padStart(8, '0')}</span>
        </div>
      )}

      <div className="bg-[#0D1017] rounded-lg border border-white/[0.06] overflow-x-auto" data-testid="hex-content">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-[#EC2229] animate-spin" />
          </div>
        ) : hexData?.rows ? (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] text-slate-600 uppercase tracking-wider border-b border-white/[0.04]">
                <th className="text-left px-3 py-1.5 font-medium">Offset</th>
                <th className="text-left px-3 py-1.5 font-medium">Hex</th>
                <th className="text-left px-3 py-1.5 font-medium">ASCII</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[11px] leading-5">
              {hexData.rows.map((row, i) => (
                <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-3 py-0.5 text-[#EC2229]/60 select-none whitespace-nowrap">{row.offset}</td>
                  <td className="px-3 py-0.5 text-slate-300 whitespace-nowrap">{row.hex}</td>
                  <td className="px-3 py-0.5 text-emerald-400/70 whitespace-nowrap">{row.ascii}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center">Keine Daten verfügbar</p>
        )}
      </div>

      {/* Navigation */}
      {hexData && (
        <div className="flex items-center justify-between mt-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fetchHex(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || loading}
            className="text-xs text-slate-400 hover:text-white"
            data-testid="hex-prev"
          >
            <ChevronLeft className="w-3 h-3 mr-1" /> Zurück
          </Button>
          <span className="text-[10px] text-slate-600">
            {offset} - {offset + (hexData?.length || PAGE_SIZE)} / {hexData?.file_size || 0}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fetchHex(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= (hexData?.file_size || 0) || loading}
            className="text-xs text-slate-400 hover:text-white"
            data-testid="hex-next"
          >
            Weiter <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ======================== TEAM NOTES COMPONENT ========================
const TeamNotes = ({ fileId }) => {
  const [notes, setNotes] = useState([]);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const CATEGORIES = [
    { id: 'general', label: 'Allgemein', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
    { id: 'tuning', label: 'Tuning', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    { id: 'problem', label: 'Problem', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
    { id: 'info', label: 'Info', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  ];

  const fetchNotes = useCallback(async () => {
    try {
      const res = await filesAPI.getTeamNotes(fileId);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Failed to load team notes:', err);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAddNote = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message);
      formData.append('category', category);
      await filesAPI.addTeamNote(fileId, formData);
      setMessage('');
      setCategory('general');
      fetchNotes();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await filesAPI.deleteTeamNote(fileId, noteId);
      fetchNotes();
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const getCategoryStyle = (cat) => CATEGORIES.find(c => c.id === cat)?.color || CATEGORIES[0].color;
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.id === cat)?.label || 'Allgemein';

  return (
    <div className="glass-card p-5 border-amber-500/10" data-testid="team-notes">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
        <StickyNote className="w-4 h-4 text-amber-400" />
        Team-Notizen
        <span className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded-full">Intern</span>
      </h3>

      {/* Notes List */}
      <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1 mb-4 scrollbar-thin">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-slate-500 py-4 text-center">Noch keine Team-Notizen</p>
        ) : (
          notes.map(note => (
            <div key={note.id} className="bg-white/[0.03] rounded-lg p-3 border border-white/[0.04] group" data-testid={`team-note-${note.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-white">{note.user_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${getCategoryStyle(note.category)}`}>
                      {getCategoryLabel(note.category)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">{note.message}</p>
                  <p className="text-[10px] text-slate-600 mt-1">
                    {new Date(note.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(note.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400 p-1"
                  data-testid={`delete-note-${note.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Category Picker */}
      <div className="flex gap-1.5 mb-2">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={`text-[10px] px-2 py-1 rounded-full border transition-all ${category === cat.id ? cat.color : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:text-slate-300'}`}
            data-testid={`note-category-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Interne Notiz..."
          className="input-field flex-1 min-h-[60px] text-sm"
          rows={2}
          data-testid="team-note-input"
        />
        <Button
          onClick={handleAddNote}
          disabled={sending || !message.trim()}
          className="btn-primary self-end"
          data-testid="add-team-note"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

// ======================== FILE TIMELINE COMPONENT ========================
const FileTimeline = ({ fileId, isStaff }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showReclamationModal, setShowReclamationModal] = useState(false);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await filesAPI.getTimeline(fileId);
      setTimeline(res.data);
    } catch (err) { console.error('Failed to load timeline:', err); }
    finally { setLoading(false); }
  }, [fileId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const eventConfig = {
    created: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
    version: { icon: GitBranch, color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
    comment: { icon: MessageSquare, color: 'text-slate-400', bg: 'bg-slate-500/20', border: 'border-slate-500/30' },
    status_change: { icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
    reclamation: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' },
    dyno: { icon: BarChart3, color: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/30' },
  };

  return (
    <div className="glass-card p-5" data-testid="file-timeline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-[#EC2229]" />
          Verlauf / Timeline
          {timeline && <span className="text-[10px] text-slate-500 font-normal ml-1">({timeline.events?.length || 0} Einträge)</span>}
        </h3>
        <div className="flex items-center gap-2">
          {isStaff && (
            <>
              <Button size="sm" variant="ghost" onClick={() => setShowVersionModal(true)} className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="add-version-btn">
                <Plus className="w-3 h-3 mr-1" /> Version
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowReclamationModal(true)} className="text-xs text-red-400 hover:text-red-300" data-testid="add-reclamation-btn">
            <AlertTriangle className="w-3 h-3 mr-1" /> Reklamation
          </Button>
          <button onClick={() => setExpanded(!expanded)} className="text-slate-500 hover:text-white">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      {timeline && (
        <div className="flex gap-3 mb-4">
          <div className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium">
            {timeline.total_versions} Version{timeline.total_versions !== 1 ? 'en' : ''}
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${timeline.total_reclamations > 0 ? 'bg-red-500/10 text-red-400' : 'bg-slate-500/10 text-slate-500'}`}>
            {timeline.total_reclamations} Reklamation{timeline.total_reclamations !== 1 ? 'en' : ''}
          </div>
        </div>
      )}

      {expanded && (
        <>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#EC2229] animate-spin" /></div>
          ) : !timeline?.events?.length ? (
            <p className="text-sm text-slate-500 text-center py-6">Noch keine Einträge</p>
          ) : (
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-white/[0.06]" />

              <div className="space-y-3">
                {timeline.events.map((event, i) => {
                  const config = eventConfig[event.type] || eventConfig.comment;
                  const Icon = config.icon;
                  return (
                    <div key={i} className="relative pl-10" data-testid={`timeline-event-${i}`}>
                      {/* Dot */}
                      <div className={`absolute left-2.5 top-2 w-3 h-3 rounded-full ${config.bg} border ${config.border}`} />

                      <div className={`p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors ${event.type === 'reclamation' ? 'border-red-500/20 bg-red-500/[0.03]' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Icon className={`w-3.5 h-3.5 ${config.color} flex-shrink-0`} />
                              <span className="text-sm font-medium text-white">{event.title}</span>
                              {event.version_number && (
                                <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded-full">v{event.version_number}</span>
                              )}
                              {event.is_staff && <span className="text-[10px] bg-[#EC2229]/10 text-[#EC2229] px-1.5 py-0.5 rounded-full">Staff</span>}
                            </div>
                            {event.details && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{event.details}</p>}

                            {/* Version-specific: Dyno data */}
                            {event.type === 'version' && (event.dyno_before || event.dyno_after) && (
                              <div className="flex gap-4 mt-2 text-xs">
                                {event.dyno_before && (
                                  <span className="text-slate-400">Vorher: <strong className="text-white">{event.dyno_before.ps}PS / {event.dyno_before.nm}Nm</strong></span>
                                )}
                                {event.dyno_after && (
                                  <span className="text-emerald-400">Nachher: <strong className="text-white">{event.dyno_after.ps}PS / {event.dyno_after.nm}Nm</strong></span>
                                )}
                              </div>
                            )}

                            {/* Attachments */}
                            {event.attachments?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {event.attachments.map(att => (
                                  <div key={att.id} className="flex flex-col gap-1">
                                    <span className="inline-flex items-center gap-1 text-[10px] bg-white/5 border border-white/[0.06] px-2 py-0.5 rounded-full text-slate-400">
                                      <Paperclip className="w-2.5 h-2.5" />
                                      {att.filename}
                                      {att.type === 'log' && (
                                        <a
                                          href={`/dyno?file_id=${fileId}&version_id=${event.version_id}`}
                                          className="ml-1 text-[#EC2229] hover:text-[#EC2229]/80 font-medium"
                                          onClick={e => e.stopPropagation()}
                                          data-testid={`dyno-link-${att.id}`}
                                        >
                                          <Gauge className="w-2.5 h-2.5 inline" /> Dyno
                                        </a>
                                      )}
                                    </span>
                                    {att.dyno_peak && (
                                      <span className="inline-flex items-center gap-1.5 text-[10px] bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full text-emerald-400">
                                        <Gauge className="w-2.5 h-2.5" />
                                        {att.dyno_peak.power_ps?.toFixed(1)} PS / {att.dyno_peak.torque_nm?.toFixed(1)} Nm
                                        {att.log_evaluation?.overall_status === 'warning' && <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />}
                                        {att.log_evaluation?.overall_status === 'ok' && <Check className="w-2.5 h-2.5 text-emerald-400" />}
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {event.has_tuned_file && (
                                  <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full text-emerald-400">
                                    <FileText className="w-2.5 h-2.5" /> Tuned File
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Reclamation resolution */}
                            {event.type === 'reclamation' && event.resolution && (
                              <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                                <p className="text-[10px] text-emerald-500 font-medium">Gelöst:</p>
                                <p className="text-xs text-slate-300">{event.resolution}</p>
                              </div>
                            )}
                            {event.type === 'reclamation' && event.status === 'open' && isStaff && (
                              <span className="inline-block mt-1 text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">Offen — wartet auf Lösung</span>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-600 whitespace-nowrap flex-shrink-0">
                            {formatDate(event.timestamp)}
                          </span>
                        </div>
                        {event.user && <p className="text-[10px] text-slate-600 mt-1">von {event.user}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* New Version Modal - portal to escape glass-card stacking context */}
      {showVersionModal && createPortal(
        <NewVersionModal fileId={fileId} onClose={() => setShowVersionModal(false)} onCreated={fetchTimeline} />,
        document.body
      )}

      {/* Reclamation Modal - portal to escape glass-card stacking context */}
      {showReclamationModal && createPortal(
        <ReclamationModal fileId={fileId} onClose={() => setShowReclamationModal(false)} onCreated={fetchTimeline} />,
        document.body
      )}
    </div>
  );
};

// ======================== NEW VERSION MODAL ========================
const NewVersionModal = ({ fileId, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tunedFile, setTunedFile] = useState(null);
  const [dynoFile, setDynoFile] = useState(null);
  const [logFile, setLogFile] = useState(null);
  const [errorLog, setErrorLog] = useState(null);
  const [dynoBefore, setDynoBefore] = useState({ ps: '', nm: '' });
  const [dynoAfter, setDynoAfter] = useState({ ps: '', nm: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description);
      if (tunedFile) fd.append('tuned_file', tunedFile);
      if (dynoFile) fd.append('dyno_file', dynoFile);
      if (logFile) fd.append('log_file', logFile);
      if (errorLog) fd.append('error_log', errorLog);
      if (dynoBefore.ps) fd.append('dyno_before_ps', dynoBefore.ps);
      if (dynoAfter.ps) fd.append('dyno_after_ps', dynoAfter.ps);
      if (dynoBefore.nm) fd.append('dyno_before_nm', dynoBefore.nm);
      if (dynoAfter.nm) fd.append('dyno_after_nm', dynoAfter.nm);
      await filesAPI.createVersion(fileId, fd);
      onCreated();
      onClose();
    } catch (err) { console.error('Failed to create version:', err); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="glass-card p-6 w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-version-modal">
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-emerald-400" /> Neue Version erstellen
        </h3>
        <div className="space-y-3">
          <div>
            <Label className="label-text">Titel *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Stage 1, Pop&Bang hinzugefügt, Feinabstimmung..." className="input-field" data-testid="version-title" />
          </div>
          <div>
            <Label className="label-text">Beschreibung</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Details zur Änderung..." className="input-field" rows={2} data-testid="version-desc" />
          </div>
          <div>
            <Label className="label-text">Tuned File</Label>
            <Input type="file" onChange={e => setTunedFile(e.target.files?.[0])} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-emerald-500/20 file:text-emerald-400 file:rounded-md file:px-2 file:py-1" data-testid="version-tuned-file" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-text">Dyno Chart / Bild</Label>
              <Input type="file" accept=".png,.jpg,.jpeg,.pdf,.webp" onChange={e => setDynoFile(e.target.files?.[0])} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-purple-500/20 file:text-purple-400 file:rounded-md file:px-2 file:py-1" data-testid="version-dyno-file" />
            </div>
            <div>
              <Label className="label-text">Log-Datei</Label>
              <Input type="file" onChange={e => setLogFile(e.target.files?.[0])} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-blue-500/20 file:text-blue-400 file:rounded-md file:px-2 file:py-1" data-testid="version-log-file" />
            </div>
          </div>
          <div>
            <Label className="label-text">Fehlerspeicher-Export</Label>
            <Input type="file" onChange={e => setErrorLog(e.target.files?.[0])} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-amber-500/20 file:text-amber-400 file:rounded-md file:px-2 file:py-1" data-testid="version-error-log" />
          </div>

          {/* Dyno Values */}
          <div className="p-3 bg-white/[0.03] rounded-lg border border-white/[0.06]">
            <Label className="label-text flex items-center gap-2 mb-2">
              <Gauge className="w-3.5 h-3.5 text-purple-400" /> Leistungsdaten (optional)
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Vorher</p>
                <div className="flex gap-2">
                  <Input type="number" value={dynoBefore.ps} onChange={e => setDynoBefore(p => ({ ...p, ps: e.target.value }))} placeholder="PS" className="input-field text-sm" />
                  <Input type="number" value={dynoBefore.nm} onChange={e => setDynoBefore(p => ({ ...p, nm: e.target.value }))} placeholder="Nm" className="input-field text-sm" />
                </div>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Nachher</p>
                <div className="flex gap-2">
                  <Input type="number" value={dynoAfter.ps} onChange={e => setDynoAfter(p => ({ ...p, ps: e.target.value }))} placeholder="PS" className="input-field text-sm" />
                  <Input type="number" value={dynoAfter.nm} onChange={e => setDynoAfter(p => ({ ...p, nm: e.target.value }))} placeholder="Nm" className="input-field text-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="btn-secondary flex-1">Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()} className="btn-primary flex-1" data-testid="submit-version">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Version erstellen
          </Button>
        </div>
      </div>
    </div>
  );
};

// ======================== RECLAMATION MODAL ========================
const ReclamationModal = ({ fileId, onClose, onCreated }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [logFile, setLogFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('description', description);
      if (logFile) fd.append('log_file', logFile);
      await filesAPI.createReclamation(fileId, fd);
      onCreated();
      onClose();
    } catch (err) { console.error('Failed to create reclamation:', err); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="glass-card p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()} data-testid="reclamation-modal">
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" /> Reklamation einreichen
        </h3>
        <div className="space-y-3">
          <div>
            <Label className="label-text">Betreff *</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="z.B. Ruckeln bei Teillast" className="input-field" data-testid="reclamation-subject" />
          </div>
          <div>
            <Label className="label-text">Beschreibung *</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Bitte beschreiben Sie das Problem so genau wie möglich..." className="input-field" rows={4} data-testid="reclamation-desc" />
          </div>
          <div>
            <Label className="label-text">Log-Datei (optional)</Label>
            <Input type="file" onChange={e => setLogFile(e.target.files?.[0])} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-red-500/20 file:text-red-400 file:rounded-md file:px-2 file:py-1" data-testid="reclamation-log-file" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="btn-secondary flex-1">Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={submitting || !subject.trim() || !description.trim()} className="btn-primary flex-1" data-testid="submit-reclamation">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Reklamation einreichen
          </Button>
        </div>
      </div>
    </div>
  );
};

// ======================== MAIN PAGE ========================
const FileDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale } = useI18n();
  
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketPriority, setTicketPriority] = useState('normal');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [dynoFile, setDynoFile] = useState(null);
  const [dynoDesc, setDynoDesc] = useState('');
  const [uploadingDyno, setUploadingDyno] = useState(false);
  const [dynoDiagrams, setDynoDiagrams] = useState([]);
  const [tunedFile, setTunedFile] = useState(null);
  const [uploadingTuned, setUploadingTuned] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');

  const isStaff = user?.role === 'admin' || user?.role === 'operator';

  const fetchFile = async () => {
    try {
      const response = await filesAPI.get(id);
      setFile(response.data);
    } catch (error) {
      console.error('Failed to fetch file:', error);
      navigate('/files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFile();
    const interval = setInterval(fetchFile, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await ticketsAPI.list();
      setTickets((res.data || []).filter(t => t.file_id === id));
    } catch (err) { console.error(err); }
  }, [id]);

  const fetchDynos = useCallback(async () => {
    try {
      const res = await dynoAPI.list(id);
      setDynoDiagrams(res.data || []);
    } catch (err) { console.error(err); }
  }, [id]);

  useEffect(() => { fetchTickets(); fetchDynos(); }, [id, fetchTickets, fetchDynos]);

  const handleDownload = async (type) => {
    try {
      const response = await filesAPI.download(id, type);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.ticket_id}_${type}.bin`;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleSendComment = async () => {
    if (!comment.trim()) return;
    setSendingComment(true);
    try {
      await filesAPI.addComment(id, comment);
      setComment('');
      await fetchFile();
    } catch (err) {
      console.error('Failed to send comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionReason.trim()) return;
    try {
      await filesAPI.requestRevision(id, revisionReason);
      setShowRevisionModal(false);
      setRevisionReason('');
      await fetchFile();
    } catch (err) { console.error('Failed to request revision:', err); }
  };

  const handleCreateTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) return;
    setCreatingTicket(true);
    try {
      await ticketsAPI.create({ file_id: id, subject: ticketSubject, message: ticketMessage, priority: ticketPriority });
      setShowTicketModal(false);
      setTicketSubject(''); setTicketMessage(''); setTicketPriority('normal');
      fetchTickets();
    } catch (err) { console.error('Failed to create ticket:', err); }
    finally { setCreatingTicket(false); }
  };

  const handleUploadDyno = async () => {
    if (!dynoFile) return;
    setUploadingDyno(true);
    try {
      const formData = new FormData();
      formData.append('file', dynoFile);
      formData.append('description', dynoDesc);
      await dynoAPI.upload(id, formData);
      setDynoFile(null); setDynoDesc('');
      fetchDynos();
    } catch (err) { console.error('Failed to upload dyno:', err); alert(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setUploadingDyno(false); }
  };

  const handleUploadTuned = async () => {
    if (!tunedFile) return;
    setUploadingTuned(true);
    try {
      const formData = new FormData();
      formData.append('file', tunedFile);
      await adminAPI.uploadTunedFile(id, formData);
      setTunedFile(null);
      await fetchFile();
    } catch (err) {
      console.error('Failed to upload tuned file:', err);
      alert(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingTuned(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await adminAPI.updateFileStatus(id, newStatus);
      await fetchFile();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10', label: t('files.pending') },
    in_progress: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-500/10', label: t('files.inProgress') },
    completed: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: t('files.completed') },
    revision_requested: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: t('files.revisionRequested') },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!file) return null;

  const status = statusConfig[file.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="file-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/files')} className="btn-ghost" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-white font-mono">{file.ticket_id}</h1>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${status.bg}`}>
                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
              </div>
            </div>
            <p className="text-slate-400 mt-1">{file.vehicle_brand} {file.vehicle_model} ({file.vehicle_year})</p>
          </div>
          <div className="flex gap-2">
            {isStaff && (
              <Button variant="outline" onClick={() => handleDownload('original')} className="btn-secondary" data-testid="download-original">
                <Download className="w-4 h-4 mr-2" /> Original
              </Button>
            )}
            {file.status === 'completed' && (
              <>
                {!isStaff && (
                  <Button variant="outline" onClick={() => handleDownload('original')} className="btn-secondary" data-testid="download-original">
                    <Download className="w-4 h-4 mr-2" /> Original
                  </Button>
                )}
                <Button onClick={() => handleDownload('tuned')} className="btn-primary" data-testid="download-tuned">
                  <Download className="w-4 h-4 mr-2" /> Tuned
                </Button>
                <Button variant="outline" onClick={() => setShowRevisionModal(true)} className="btn-secondary" data-testid="request-revision">
                  Revision anfordern
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vehicle & Tool Info */}
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">Fahrzeug & Tool Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fahrzeug</p>
                  <p className="text-white font-medium">{file.vehicle_brand} {file.vehicle_model}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Baujahr</p>
                  <p className="text-white font-medium">{file.vehicle_year}</p>
                </div>
                {file.engine_code && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Motorcode</p>
                    <p className="text-white font-mono">{file.engine_code}</p>
                  </div>
                )}
                {file.ecu_type && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">ECU Typ</p>
                    <p className="text-white font-mono">{file.ecu_type}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tool</p>
                  <p className="text-white font-medium">{file.tool_type}</p>
                </div>
                {file.tool_serial && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Seriennummer</p>
                    <p className="text-white font-mono">{file.tool_serial}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">Gewählte Optionen</h2>
              <div className="flex flex-wrap gap-2">
                {file.options?.map(opt => (
                  <span key={opt} className="badge-neutral px-3 py-1.5">{opt.replace('_', ' ').toUpperCase()}</span>
                ))}
              </div>
              {file.notes && (
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Hinweise</p>
                  <p className="text-slate-300">{file.notes}</p>
                </div>
              )}
            </div>

            {/* HEX Viewer */}
            <HexViewer fileId={id} />

            {/* File Database Matching (Admin only - B1) */}
            {isStaff && <FileMatchingPanel fileId={id} />}

            {/* File Timeline / Versioning */}
            <FileTimeline fileId={id} isStaff={isStaff} />

            {/* Timeline / Chat */}
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Kommunikation
              </h2>
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {file.comments?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Noch keine Nachrichten</p>
                ) : (
                  file.comments?.map(c => (
                    <div key={c.id} className={`flex gap-3 ${c.is_staff ? '' : 'flex-row-reverse'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${c.is_staff ? 'bg-[#EC2229]/20' : 'bg-blue-500/20'}`}>
                        <User className={`w-4 h-4 ${c.is_staff ? 'text-[#EC2229]' : 'text-blue-500'}`} />
                      </div>
                      <div className={`flex-1 p-4 rounded-xl max-w-md ${c.is_staff ? 'bg-[#EC2229]/10 border border-[#EC2229]/20' : 'bg-white/5'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white">{c.user_name}</span>
                          {c.is_staff && <span className="badge-error text-xs">Staff</span>}
                        </div>
                        <p className="text-slate-300 text-sm">{c.message}</p>
                        <p className="text-xs text-slate-500 mt-2">{formatDate(c.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-3">
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Nachricht eingeben..." className="input-field flex-1" data-testid="comment-input" />
                <Button onClick={handleSendComment} disabled={sendingComment || !comment.trim()} className="btn-primary self-end" data-testid="send-comment">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="glass-card p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">Status</h3>
              <div className={`p-4 rounded-xl ${status.bg} mb-4`}>
                <div className="flex items-center gap-3">
                  <StatusIcon className={`w-6 h-6 ${status.color}`} />
                  <span className={`font-medium ${status.color}`}>{status.label}</span>
                </div>
                {file.queue_position && file.status === 'pending' && (
                  <p className="text-sm text-slate-400 mt-2">Position in Warteschlange: #{file.queue_position}</p>
                )}
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Erstellt</span>
                  <span className="text-white">{formatDate(file.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Aktualisiert</span>
                  <span className="text-white">{formatDate(file.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Admin Actions Panel */}
            {isStaff && (
              <div className="glass-card p-6 border-[#EC2229]/20" data-testid="admin-actions">
                <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#EC2229]" /> Admin-Aktionen
                </h3>

                {/* Status Change */}
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Status ändern</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'pending', label: 'Wartend', color: 'bg-amber-500/10 border-amber-500/30 text-amber-400' },
                      { id: 'in_progress', label: 'In Bearbeitung', color: 'bg-blue-500/10 border-blue-500/30 text-blue-400' },
                      { id: 'completed', label: 'Fertig', color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' },
                      { id: 'revision_requested', label: 'Revision', color: 'bg-orange-500/10 border-orange-500/30 text-orange-400' },
                    ].map(s => (
                      <button
                        key={s.id}
                        onClick={() => handleStatusChange(s.id)}
                        disabled={file.status === s.id}
                        className={`py-2 px-3 rounded-lg text-xs font-medium border transition-all ${file.status === s.id ? s.color + ' ring-1 ring-white/20' : 'bg-white/5 border-white/10 text-slate-500 hover:text-white hover:border-white/20'}`}
                        data-testid={`status-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Upload Tuned File */}
                <div className="pt-4 border-t border-white/[0.06]">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Bearbeitete Datei zurückschicken</p>
                  <label className="block mb-2">
                    <Input
                      type="file"
                      onChange={e => setTunedFile(e.target.files?.[0] || null)}
                      className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-[#EC2229]/20 file:text-[#EC2229] file:rounded-md file:px-2 file:py-1"
                      data-testid="tuned-file-input"
                    />
                  </label>
                  {tunedFile && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                        <FileText className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm text-slate-300 truncate">{tunedFile.name}</span>
                        <span className="text-xs text-slate-500 ml-auto">{(tunedFile.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <Button
                        onClick={handleUploadTuned}
                        disabled={uploadingTuned}
                        className="btn-primary w-full"
                        data-testid="upload-tuned-btn"
                      >
                        {uploadingTuned ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Tuned Datei hochladen & abschließen
                      </Button>
                    </div>
                  )}
                  {file.tuned_filename && (
                    <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Tuned Datei vorhanden</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Price Card */}
            <div className="glass-card p-6 bg-[#EC2229]/5 border-[#EC2229]/20">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">Kosten</h3>
              <p className="text-3xl font-heading font-bold text-white">
                {(file.credit_cost || 0).toFixed(1)} <span className="text-lg text-slate-400">Credits</span>
              </p>
              <p className="text-sm text-slate-400 mt-1">Bereits abgebucht</p>
            </div>

            {/* File Info */}
            <div className="glass-card p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" /> Datei Info
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-slate-500">Original Datei</p>
                  <p className="text-white font-mono text-xs truncate">{file.original_filename}</p>
                </div>
                <div>
                  <p className="text-slate-500">Dateigröße</p>
                  <p className="text-white font-mono">{(file.file_size / 1024).toFixed(2)} KB</p>
                </div>
                <div>
                  <p className="text-slate-500">SHA256</p>
                  <p className="text-white font-mono text-xs truncate">{file.file_hash}</p>
                </div>
              </div>
            </div>

            {/* Team Notes (Admin Only) */}
            {isStaff && <TeamNotes fileId={id} />}

            {/* Support Ticket */}
            <div className="glass-card p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-[#EC2229]" /> Support
              </h3>
              {tickets.length > 0 && (
                <div className="space-y-2 mb-3">
                  {tickets.map(t => (
                    <div key={t.id} className="p-2 bg-white/5 rounded-lg border border-white/5">
                      <p className="text-xs font-mono text-[#EC2229]">{t.ticket_number}</p>
                      <p className="text-sm text-white truncate">{t.subject}</p>
                      <p className="text-xs text-slate-500">{t.status === 'open' ? 'Offen' : t.status === 'answered' ? 'Beantwortet' : 'Geschlossen'}</p>
                    </div>
                  ))}
                </div>
              )}
              <Button size="sm" onClick={() => setShowTicketModal(true)} className="btn-primary w-full" data-testid="create-ticket-btn">
                <LifeBuoy className="w-4 h-4 mr-2" /> Ticket erstellen
              </Button>
            </div>

            {/* Dyno Diagram Upload */}
            <div className="glass-card p-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-emerald-400" /> Dyno-Diagramme
              </h3>
              {dynoDiagrams.length > 0 && (
                <div className="space-y-2 mb-3">
                  {dynoDiagrams.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/5">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{d.filename}</p>
                        <p className="text-xs text-slate-500">{d.description || 'Kein Kommentar'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <label className="block">
                  <Input type="file" accept=".png,.jpg,.jpeg,.pdf,.webp" onChange={e => setDynoFile(e.target.files?.[0] || null)} className="input-field text-sm file:mr-2 file:text-xs file:border-0 file:bg-[#EC2229]/20 file:text-[#EC2229] file:rounded-md file:px-2 file:py-1" data-testid="dyno-file-input" />
                </label>
                {dynoFile && (
                  <>
                    <Input value={dynoDesc} onChange={e => setDynoDesc(e.target.value)} placeholder="Beschreibung (optional)" className="input-field text-sm" data-testid="dyno-desc-input" />
                    <Button size="sm" onClick={handleUploadDyno} disabled={uploadingDyno} className="btn-primary w-full" data-testid="upload-dyno-btn">
                      {uploadingDyno ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Dyno hochladen
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Revision Modal */}
        {showRevisionModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="revision-modal">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">Revision anfordern</h3>
              <p className="text-slate-400 text-sm mb-4">Bitte beschreiben Sie, warum Sie eine Überarbeitung benötigen.</p>
              <Textarea value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)} placeholder="Grund für die Revision..." className="input-field mb-4" data-testid="revision-reason" />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowRevisionModal(false)} className="btn-secondary flex-1">Abbrechen</Button>
                <Button onClick={handleRequestRevision} disabled={!revisionReason.trim()} className="btn-primary flex-1" data-testid="submit-revision">Absenden</Button>
              </div>
            </div>
          </div>
        )}

        {/* Support Ticket Modal */}
        {showTicketModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="glass-card p-6 w-full max-w-lg mx-4" data-testid="ticket-modal">
              <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-[#EC2229]" /> Support-Ticket erstellen
              </h3>
              <p className="text-slate-400 text-sm mb-4">Ticket für: <span className="text-[#EC2229] font-mono">{file.ticket_id}</span></p>
              <div className="space-y-4">
                <Input value={ticketSubject} onChange={e => setTicketSubject(e.target.value)} placeholder="Betreff" className="input-field" data-testid="ticket-subject" />
                <Textarea value={ticketMessage} onChange={e => setTicketMessage(e.target.value)} placeholder="Beschreiben Sie Ihr Anliegen..." className="input-field min-h-[120px]" data-testid="ticket-message" />
                <div className="flex gap-2">
                  {['low', 'normal', 'high'].map(p => (
                    <button key={p} onClick={() => setTicketPriority(p)} className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${ticketPriority === p ? (p === 'high' ? 'bg-red-500/10 border-red-500/30 text-red-400' : p === 'normal' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-500/10 border-slate-500/30 text-slate-400') : 'bg-white/5 border-white/10 text-slate-500 hover:text-white'}`} data-testid={`ticket-priority-${p}`}>
                      {p === 'high' ? 'Hoch' : p === 'normal' ? 'Normal' : 'Niedrig'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowTicketModal(false)} className="btn-secondary flex-1">Abbrechen</Button>
                <Button onClick={handleCreateTicket} disabled={creatingTicket || !ticketSubject.trim() || !ticketMessage.trim()} className="btn-primary flex-1" data-testid="submit-ticket">
                  {creatingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Ticket erstellen
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FileDetailPage;
